<template>
  <BaseWidget
    title="Support chat"
    :show-info-icon="true"
    info-tooltip="Support chats are group conversations between a user and any admin. Shows the most recent conversations."
    :info-tooltip-link="{ url: 'https://support.optixapp.com/en/articles/13260740-what-is-inbox#h_5b2fc50180', text: 'Learn about Support Chat' }"
    :show-settings="false"
    :disable-date-range="true"
    :edit-mode="editMode"
    :widget-id="widgetId"
    :widget-type="widgetType"
    :row-span="rowSpan"
    :col-span="colSpan"
    @remove-widget="$emit('remove-widget', widgetId)"
  >
    <div
      v-if="loading"
      class="loading-state"
    >
      <SkeletonLoader
        type="widget"
        :compact="focused"
        :show-table="!focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      <RateLimitError v-if="isRateLimitError(error)" />
      <span v-else>Error: {{ error.message }}</span>
    </div>
    <div
      v-else
      class="widget-content-wrapper"
      :class="{ 'focused': focused }"
    >
      <MetricValue
        :value="unreadCount.toString()"
        subtitle="Conversations"
        :has-data-table="topConversations.length > 0 && !focused"
      >
        <template #comparison>
          <span
            class="unread-text"
            :class="{ 'unread-text--zero': unreadCount === 0 }"
          >Unread</span>
        </template>
        <template
          v-if="hasMore"
          #subtitle-suffix
        >
          <span
            class="focused-view-all-link"
            @click="openViewAllPanel"
          >View all</span>
        </template>
      </MetricValue>
      <div
        v-if="!focused && topConversations.length > 0"
        class="conversations-list"
      >
        <div
          v-for="(conversation, index) in topConversations"
          :key="conversation.conversation_id || index"
          class="conversation-item"
          @click="handleConversationClickGuarded(conversation)"
        >
          <div class="conversation-row">
            <div class="conversation-avatar-wrapper">
              <div
                v-if="conversation.unread_message_count > 0"
                class="unread-dot"
              />
              <div class="conversation-image">
                <img
                  v-if="conversation.avatar"
                  :src="conversation.avatar"
                  :alt="conversation.name"
                >
                <div
                  v-else
                  class="image-placeholder"
                >
                  {{ getInitials(conversation.name) }}
                </div>
              </div>
            </div>
            <div class="conversation-info">
              <span class="conversation-name">{{ conversation.name || 'Unnamed Conversation' }}</span>
              <span class="conversation-snippet">{{ getMessageSnippet(conversation) }}</span>
            </div>
          </div>
        </div>
      </div>
      <div
        v-else-if="!loading && topConversations.length === 0"
        class="no-conversations"
      >
        No recent conversations
      </div>
    </div>
  </BaseWidget>
</template>

<script setup>
import { computed, onMounted, onUnmounted } from 'vue'
import BaseWidget from './BaseWidget.vue'
import MetricValue from './MetricValue.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import RateLimitError from './RateLimitError.vue'
import { useGraphQLData } from '../composables/useWidgetData.js'
import { getDemoSupportChatData } from '../services/demoData.js'
import { isRateLimitError } from '../utils/errorUtils.js'
import { getAvatarUrl } from '../utils/avatar.js'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'
import { pushConversationPanel, pushAdminSidePanel } from '../composables/useOptixPanel.js'
import { getOrganizationIdFromURL } from '../utils/tokenHandler.js'

const props = defineProps({
  priority: {
    type: Number,
    default: null
  },
  focused: {
    type: Boolean,
    default: false
  },
  editMode: {
    type: Boolean,
    default: false
  },
  widgetId: {
    type: String,
    default: null
  },
  rowSpan: {
    type: Number,
    default: 1
  },
  colSpan: {
    type: Number,
    default: 1
  },
  sectionId: {
    type: String,
    default: null
  },
  widgetType: {
    type: String,
    default: null
  }
})

defineEmits(['remove-widget'])

const organizationId = computed(() => getOrganizationIdFromURL())

const supportChatQuery = `
  query SupportChatConversations(
    $organizationId: ID
    $conversationType: [ConversationType!]
    $order: ConversationOrder
  ) {
    conversations(
      organization_id: $organizationId
      conversation_type: $conversationType
      order: $order
    ) {
      total
      unread_conversation_count
      data {
        conversation_id
        name
        message_count
        latest_message {
          user_id
          message
          plaintext
          timestamp
        }
        image {
          url
        }
        unread_message_count
        conversation_type
        users
      }
    }
  }
`

const { data, loading, error, cleanup, refresh } = useGraphQLData({
  dataKey: 'support_chat_conversations',
  query: supportChatQuery,
  operationName: 'SupportChatConversations',
  variables: computed(() => ({
    organizationId: organizationId.value || undefined,
    conversationType: ['USER_TO_ADMINS'],
    order: 'CONVERSATION_SEQUENCE_DESC'
  })),
  priority: props.priority ?? 1,
  getDemoData: () => getDemoSupportChatData()
})

const { data: meData } = useGraphQLData({
  dataKey: 'support_chat_current_user',
  query: `query GetCurrentUser { me { user { user_id } } }`,
  operationName: 'GetCurrentUser',
  variables: {},
  priority: 10,
  enableBatching: true,
  autoFetch: true
})

const currentUserId = computed(() => meData.value?.me?.user?.user_id || '')

const conversations = computed(() => {
  const items = data.value?.conversations?.data ?? []
  return items
    .filter((item) => {
      // Hide conversations without a user (e.g. "[org] team" with no "&")
      const name = item.name || ''
      if (!name.includes('&')) return false
      return true
    })
    .map((item) => ({
      conversation_id: item.conversation_id,
      name: item.name || 'Unnamed Conversation',
      avatar: getAvatarUrl(item.image?.url, item.name || item.conversation_id),
      messageCount: item.message_count || 0,
      latest_message: item.latest_message || null,
      unread_message_count: item.unread_message_count || 0
    }))
})

const totalCount = computed(() => {
  const total = data.value?.conversations?.total
  if (total != null) return Math.max(0, total - 1).toString()
  return conversations.value.length.toString()
})

const unreadCount = computed(() => {
  return conversations.value.filter(c => c.unread_message_count > 0).length
})

const topConversations = computed(() => {
  return [...conversations.value]
    .sort((a, b) => {
      const timeA = a.latest_message?.timestamp ? new Date(a.latest_message.timestamp).getTime() : 0
      const timeB = b.latest_message?.timestamp ? new Date(b.latest_message.timestamp).getTime() : 0
      return timeB - timeA
    })
    .slice(0, 3)
})

const hasMore = computed(() => conversations.value.length > 3)

const openViewAllPanel = () => {
  pushAdminSidePanel('view-all-support-chat')
}

const getMessageSnippet = (conv) => {
  const msg = conv.latest_message
  if (!msg) return 'No message'
  return (msg.plaintext || msg.message || '').trim() || 'No message'
}

const { guard } = useTrialModeGuard()
const handleConversationClickGuarded = guard((conversation) => {
  if (!conversation?.conversation_id) return
  pushConversationPanel(conversation.conversation_id)
})

const getInitials = (name) => {
  if (!name) return '?'
  const words = name.split(' ')
  if (words.length >= 2) {
    return (words[0][0] + words[words.length - 1][0]).toUpperCase()
  }
  return name.charAt(0).toUpperCase()
}

// Refresh when our iframe regains focus (e.g. after closing a conversation panel)
let hasFocusRefreshed = false
const handleFocus = () => {
  if (!hasFocusRefreshed) {
    hasFocusRefreshed = true
    refresh(true)
    // Debounce: allow another refresh after 2s
    setTimeout(() => { hasFocusRefreshed = false }, 2000)
  }
}

onMounted(() => {
  window.addEventListener('focus', handleFocus)
})

onUnmounted(() => {
  window.removeEventListener('focus', handleFocus)
  cleanup()
})
</script>

<style scoped>
.widget-content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  gap: 12px;
}

.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.error-state {
  color: var(--color-red);
}

.conversations-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  flex: 1;
  overflow: visible;
  min-height: 0;
  max-height: none;
}

.conversation-item {
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
  position: relative;
  margin: 0 -4px;
  padding: 10px 4px;
  min-height: 64px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
}

.conversation-item:first-child {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.conversation-item:last-child {
  border-bottom: none;
}

.conversation-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
}

.conversation-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
}

.conversation-avatar-wrapper {
  position: relative;
  flex-shrink: 0;
}

.conversation-image {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #f0f0f0;
}

.conversation-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  font-size: 14px;
}

.conversation-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.conversation-name {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.view-all-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 50px;
  padding: 0 0;
}

.view-all-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  font-weight: 400;
  cursor: pointer;
  transition: color 0.2s ease;
}

.view-all-link:hover {
  color: #2563eb;
}

.conversation-snippet {
  font-size: 13px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.87);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.no-conversations {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.unread-text {
  font-size: 14px;
  font-weight: 500;
  color: #CB912F;
}

.unread-text--zero {
  color: rgba(0, 0, 0, 0.38);
  font-weight: 400;
}

.unread-dot {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #2563eb;
  border: 2px solid white;
  z-index: 1;
}

.focused-view-all-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s;
  flex-shrink: 0;
}

.focused-view-all-link:hover {
  color: #2563eb;
}
</style>
